import { z } from "zod";
import { RichText } from "../RichText";
import { globalSectionSchema, backgroundStyleEnum, subHeadingLevel, STANDARD_WIDTH, NARROW_WIDTH } from "../globalProps";
import { CARD_SHADOW } from "../cardStyles";
import { SectionShell } from "../SectionShell";

const stepSchema = z.object({
  title: z.string().default("Step"),
  body: z.string().default("Description of this step."),
  icon: z.string().optional(),
});

/* Per-layout container width map for steps layout */
const STEPS_WIDTH = {
  contained: "max-w-5xl mx-auto px-6",
  wide:      "max-w-6xl mx-auto px-6",
  full:      "max-w-7xl mx-auto px-6",
} as const;

export const schema = globalSectionSchema.extend({
  heading: z.string().default("How It Works"),
  subheading: z.string().optional(),
  steps: z
    .array(stepSchema)
    .default([
      {
        title: "Schedule a Consultation",
        body: "Book a time that works for you. Choose phone or video, and tell us a bit about what you need.",
      },
      {
        title: "Meet With Your Advisor",
        body: "During your session, we will review your situation, answer your questions, and outline your options.",
      },
      {
        title: "Get a Clear Plan",
        body: "Walk away with a concrete action plan, written summary, and confidence about your next steps.",
      },
    ]),
  layout: z.enum(["steps", "timeline"]).default("steps"),
  cardStyle: z.enum(["plain", "cards"]).default("plain"),
  backgroundStyle: backgroundStyleEnum.default("light"),
});

export type Props = z.infer<typeof schema>;
export const defaults: Props = schema.parse({});

/* ---------- Steps layout (horizontal @ md, vertical on mobile) ---------- */

function StepsLayout({ safe, SubH }: { safe: Props; SubH: "h2" | "h3" | "h4" }) {
  const container = STEPS_WIDTH[safe.width];
  const useCards = safe.cardStyle === "cards";
  return (
    <div className={container}>
      {/* Horizontal flow at @md, vertical on mobile */}
      <div className={`flex flex-col @md:flex-row @md:items-start ${useCards ? "gap-6" : "gap-0"}`}>
        {safe.steps.map((step, i) => (
          <div
            key={i}
            className={`flex flex-col items-center text-center flex-1 relative${useCards ? ` ${CARD_SHADOW} rounded-lg p-6 bg-white text-text` : ""}`}
          >
            {/* Connector line (between circles, not on first; hidden in card mode) */}
            {i > 0 && !useCards && (
              <>
                {/* Horizontal connector (hidden on mobile) */}
                <div className="hidden @md:block absolute top-5 right-1/2 w-full h-px bg-current opacity-15" />
                {/* Vertical connector (mobile only) */}
                <div className="@md:hidden w-px h-8 bg-current opacity-15 mb-0" />
              </>
            )}

            {/* Numbered circle */}
            <div className="w-10 h-10 rounded-full bg-primary text-white flex items-center justify-center text-sm font-bold shrink-0 relative z-10">
              {i + 1}
            </div>

            {/* Title + body */}
            <div className="mt-4 px-3">
              <RichText
                as={SubH}
                html={step.title}
                className="text-lg font-semibold mb-2"
                data-prop={`steps.${i}.title`}
              />
              <RichText
                as="p"
                html={step.body}
                className="text-sm leading-relaxed opacity-80"
                data-prop={`steps.${i}.body`}
              />
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

/* ---------- Timeline layout (vertical with left-side line) ---------- */

function TimelineLayout({ safe, SubH }: { safe: Props; SubH: "h2" | "h3" | "h4" }) {
  const container = NARROW_WIDTH[safe.width];
  return (
    <div className={container}>
      <div className="relative pl-8">
        {/* Continuous vertical line */}
        <div className="absolute left-[7px] top-2 bottom-2 w-px bg-current opacity-15" />

        {safe.steps.map((step, i) => (
          <div
            key={i}
            className={`relative ${i > 0 ? "mt-10" : ""}`}
          >
            {/* Dot on the line */}
            <div className="absolute left-[-25px] top-1.5 w-4 h-4 rounded-full bg-primary border-[3px] border-current/10" />

            <RichText
              as={SubH}
              html={step.title}
              className="text-lg font-semibold mb-2"
              data-prop={`steps.${i}.title`}
            />
            <RichText
              as="p"
              html={step.body}
              className="text-base leading-relaxed opacity-80"
              data-prop={`steps.${i}.body`}
            />
          </div>
        ))}
      </div>
    </div>
  );
}

/* ---------- Main component ---------- */

export function Component(props: Props) {
  const result = schema.safeParse(props);
  const safe = result.success ? result.data : defaults;
  const SubH = subHeadingLevel(safe.headingLevel);
  const headingContainer = STANDARD_WIDTH[safe.width];

  if (safe.steps.length === 0) return null;

  return (
    <SectionShell globals={safe} bare>
      <div className={`${headingContainer} mb-12 text-center`}>
        <RichText
          as={safe.headingLevel}
          html={safe.heading}
          className="text-3xl @md:text-[2.5rem] font-bold mb-4"
          data-prop="heading"
        />
        {safe.subheading && (
          <RichText
            as="div"
            html={safe.subheading}
            className="text-lg opacity-80 max-w-2xl mx-auto"
            data-prop="subheading"
          />
        )}
      </div>

      {safe.layout === "steps" && <StepsLayout safe={safe} SubH={SubH} />}
      {safe.layout === "timeline" && <TimelineLayout safe={safe} SubH={SubH} />}
    </SectionShell>
  );
}
